/**
 * Cancel an ACTIVE / PAST_DUE add-on (TMMPDM-0019): at the end of the term (the default — the
 * server sets `cancelAtTermEnd` and service continues to term end) or immediately.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import type { AddonCancelRequest, TenantAddonView } from '@/api/tmm/types';
import { AppSwitch } from '@/components/ui/AppSwitch';
import { formatDate } from '@/format';

const REASON_MAX = 500;

interface CancelAddonDialogProps {
  open: boolean;
  slug: string;
  addon: TenantAddonView | null;
  onClose: () => void;
  onDone: () => void;
}

export function CancelAddonDialog({ open, slug, addon, onClose, onDone }: CancelAddonDialogProps) {
  const { t } = useCopy();
  // Held here, so Esc and a click outside wait for a request that is running (as ConfirmDialog does).
  const [submitting, setSubmitting] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t('addons.cancel.title', 'Cancel {{name}}?', { name: addon?.name ?? '' })}
      </DialogTitle>
      {open && addon ? (
        <CancelAddonForm
          slug={slug}
          addon={addon}
          submitting={submitting}
          setSubmitting={setSubmitting}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function CancelAddonForm({
  slug,
  addon,
  submitting,
  setSubmitting,
  onClose,
  onDone,
}: Omit<CancelAddonDialogProps, 'open' | 'addon'> & {
  addon: TenantAddonView;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [atTermEnd, setAtTermEnd] = useState(true);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length > REASON_MAX) {
      setFieldError(t('addons.form.tooLong', 'At most {{max}} characters.', { max: REASON_MAX }));
      return;
    }
    const body: AddonCancelRequest = { atTermEnd };
    if (trimmed !== '') body.reason = trimmed;
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.cancelAddon(slug, addon.id, body);
      onDone();
    } catch (err: unknown) {
      if (isValidationFailed(err)) {
        setFieldError(validationFields(err, t).reason ?? errorCopy(err, t, ''));
      } else {
        setError(errorCopy(err, t, t('addons.actionError', 'Could not update the add-on.')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <AppSwitch
            label={t('addons.cancel.atTermEnd', 'At the end of the term')}
            checked={atTermEnd}
            onChange={setAtTermEnd}
          />
          <Typography variant="body2" color="text.secondary">
            {atTermEnd
              ? addon.termEnd
                ? t('addons.cancel.termEndCaption', 'Service continues until {{date}}.', {
                    date: formatDate(addon.termEnd, i18n.language),
                  })
                : t(
                    'addons.cancel.termEndCaptionUnknown',
                    'Service continues until the end of the current term.',
                  )
              : t('addons.cancel.immediately', 'Immediately — service stops now.')}
          </Typography>
          <TextField
            label={t('addons.cancel.reason', 'Reason (optional)')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
            multiline
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('addons.cancel.keep', 'Keep add-on')}
        </Button>
        <SubmitButton color="error" onClick={() => void submit()} busy={submitting}>
          {t('addons.cancel.submit', 'Cancel add-on')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
